import { Component, OnInit } from "@angular/core"
import { ApiService } from "@services/api.service"
import { TokenService } from "@services/token.service"
import { WindowService } from "@services/window.service"

type LoginMethod = "password" | "google" | "apple" | null

const AIVA_2_URL = "https://v2.aiva.ai"

interface LoginHandoffResponse {
    result: 1
    email: string
    loginMethod: LoginMethod
    token: string
    refreshToken: string
}

@Component({
    selector: "auth-handoff",
    templateUrl: "auth-handoff.component.html",
    styleUrls: ["auth-handoff.component.scss"],
})
export class AuthHandoffComponent implements OnInit {
    state: "loading" | "error" = "loading"

    constructor(
        private apiService: ApiService,
        private tokenService: TokenService,
        private windowService: WindowService
    ) {}

    ngOnInit(): void {
        void this.processHandoff()
    }

    async processHandoff(): Promise<void> {
        const browserWindow = this.windowService.window
        const code = new URLSearchParams(
            browserWindow.location.hash.replace(/^#/, "")
        ).get("code")

        browserWindow.history.replaceState(
            browserWindow.history.state,
            "",
            browserWindow.location.pathname + browserWindow.location.search
        )

        if (!code) {
            this.state = "error"
            return
        }

        try {
            const response = await this.apiService.request(
                "/user/loginHandoff/exchange",
                { code },
                "primary",
                "post",
                false
            )
            const handoff = this.validateResponse(response)

            this.persistCredentials(handoff)
            this.windowService.replaceLocation("/")
        } catch (_) {
            this.state = "error"
        }
    }

    returnToAiva2(): void {
        this.windowService.replaceLocation(AIVA_2_URL)
    }

    private validateResponse(response: any): LoginHandoffResponse {
        const validLoginMethods: LoginMethod[] = [
            "password",
            "google",
            "apple",
            null,
        ]
        const loginMethodIsPresent = Object.prototype.hasOwnProperty.call(
            response || {},
            "loginMethod"
        )

        if (
            response?.result !== 1 ||
            typeof response.email !== "string" ||
            response.email.trim().length === 0 ||
            typeof response.token !== "string" ||
            response.token.length === 0 ||
            typeof response.refreshToken !== "string" ||
            response.refreshToken.length === 0 ||
            !loginMethodIsPresent ||
            !validLoginMethods.includes(response.loginMethod)
        ) {
            throw new Error("Invalid login handoff response")
        }

        return {
            result: 1,
            email: response.email,
            loginMethod: response.loginMethod,
            token: response.token,
            refreshToken: response.refreshToken,
        }
    }

    private persistCredentials(handoff: LoginHandoffResponse): void {
        const keys = [
            "currentUser",
            "loggedInWith",
            "redirectToApp",
            "refreshToken",
            "token",
            "tokenLastUpdate",
        ]
        const previousValues = new Map<string, string | null>()

        keys.forEach(key => previousValues.set(key, localStorage.getItem(key)))

        try {
            if (!this.tokenService.setRefreshToken(handoff.refreshToken)) {
                throw new Error("Invalid refresh token")
            }

            this.tokenService.setToken(handoff.token)
            localStorage.setItem("currentUser", handoff.email)
            localStorage.removeItem("loggedInWith")

            if (handoff.loginMethod !== null) {
                localStorage.setItem("loggedInWith", handoff.loginMethod)
            }

            localStorage.setItem("redirectToApp", "false")
        } catch (error) {
            previousValues.forEach((value, key) => {
                if (value === null) {
                    localStorage.removeItem(key)
                } else {
                    localStorage.setItem(key, value)
                }
            })

            throw error
        }
    }
}
